/**
 * Leading characters that Excel/Google Sheets/LibreOffice may interpret as a formula
 * trigger when opening a CSV file (OWASP "CSV Injection" / "Formula Injection").
 * Free-text fields we export — e.g. a custom order item's `description` — are entered
 * by staff/clients and are not sanitized against this at write time, so it must be
 * neutralized here, at the export boundary.
 */
const CSV_FORMULA_TRIGGER_CHARS = ['=', '+', '-', '@', '\t', '\r'];

/**
 * Prefixes a cell with a single quote when it starts with a formula-trigger character,
 * so spreadsheet apps render it as literal text instead of evaluating it as a formula
 * (the standard OWASP-recommended mitigation). Excel/Sheets treat a leading `'` the
 * same way on CSV import as on manual entry: it forces text and is not displayed.
 */
function sanitizeCsvFormulaTrigger(str) {
  return str && CSV_FORMULA_TRIGGER_CHARS.includes(str[0]) ? `'${str}` : str;
}

/** Quote a CSV field when it contains a comma, quote, or newline; double any embedded quotes. */
function csvEscapeCell(value) {
  const raw = value == null ? '' : String(value);
  const str = sanitizeCsvFormulaTrigger(raw);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function rowToCsvLine(row) {
  return (row || []).map(csvEscapeCell).join(',');
}

/**
 * Builds CSV text from the same `columns`/`rows`/`totalRow` (and optional balance
 * summary) used to build the PDF export, so CSV and PDF exports always stay in sync.
 */
export function buildCsvContent({
  columns,
  rows,
  totalRow,
  balanceSummary,
  balanceSummaryRows,
  balanceSummaryTitle,
  amountColumnHeader,
  amountColumnHeaderSecondary,
} = {}) {
  const lines = [];

  if (columns?.length) lines.push(rowToCsvLine(columns));
  (rows || []).forEach((row) => lines.push(rowToCsvLine(row)));
  if (totalRow) lines.push(rowToCsvLine(totalRow));

  const summaryRows = balanceSummaryRows || (balanceSummary ? [
    ['Total Items', `$${Number(balanceSummary.totalItems || 0).toLocaleString()}`],
    ['Payments', `$${Number(balanceSummary.payments || 0).toLocaleString()}`],
    ['Amount Due', `$${Number(balanceSummary.amountDue || 0).toLocaleString()}`],
  ] : null);

  if (summaryRows?.length) {
    lines.push('');
    lines.push(rowToCsvLine([balanceSummaryTitle || 'Balance Summary']));
    const dualAmount = amountColumnHeaderSecondary && summaryRows[0]?.length === 3;
    lines.push(dualAmount
      ? rowToCsvLine(['', amountColumnHeader || 'Amount', amountColumnHeaderSecondary])
      : rowToCsvLine(['', amountColumnHeader || 'Amount']));
    summaryRows.forEach((row) => lines.push(rowToCsvLine(row)));
  }

  return lines.join('\r\n');
}

/** Swap a `.pdf` filename for `.csv`; leave an existing `.csv` alone; fall back when missing. */
export function csvFilenameFor(filename) {
  if (!filename) return 'export.csv';
  if (/\.csv$/i.test(filename)) return filename;
  return /\.pdf$/i.test(filename) ? filename.replace(/\.pdf$/i, '.csv') : `${filename}.csv`;
}

/** Downloads the same item table used for PDF export as a CSV file (UTF-8 BOM for Excel). */
export function exportItemTableToCsv(exportConfig) {
  const csvBody = buildCsvContent(exportConfig);
  const blob = new Blob(['\uFEFF', csvBody], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = csvFilenameFor(exportConfig?.filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
